import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { currentHolds, subscribeToHolds, summarise } from './dirtyRegistry';
import { planNavigation, isLeaving } from './navigationPlan';

/**
 * Stops an in-app navigation from silently discarding work in progress.
 *
 * Mounted once, inside the router. It reads the SAME shared registry the
 * reload and update guards read, so a form only has to declare its state in
 * one place to be protected everywhere.
 *
 * Deliberately NOT a window.confirm: those are banned here, and a native
 * dialog cannot show which piece of work is at stake.
 */
export const NavigationGuard: React.FC = () => {
  const { t } = useTranslation();
  const [, bump] = useState(0);
  useEffect(() => subscribeToHolds(() => bump((n) => n + 1)), []);

  const summary = summarise(currentHolds());
  const plan = planNavigation(summary);

  // The blocker function is consulted at navigation time, so it must read the
  // CURRENT holds rather than a value captured when this rendered.
  const blocker = useBlocker(
    useCallback(({ currentLocation, nextLocation }: {
      currentLocation: { pathname: string; search?: string };
      nextLocation: { pathname: string; search?: string };
    }) => {
      const now = planNavigation(summarise(currentHolds()));
      return now !== null && isLeaving(currentLocation, nextLocation);
    }, []),
  );

  // Work finished while the dialog was open — the reason to stop them is
  // gone, so let the navigation they already asked for continue rather than
  // making them ask twice.
  useEffect(() => {
    if (blocker.state === 'blocked' && plan === null) blocker.proceed();
  }, [blocker, plan]);

  // ⚠️ The router can only block navigations IT performs. Pressing reload,
  // closing the tab, or going Back to a page loaded before this one are
  // cross-document navigations — the document is torn down and useBlocker
  // never sees them. Only beforeunload can speak there.
  //
  // Verified on dev: without this, Back out of the receipt editor to a
  // previously loaded page discarded the typing with no warning at all.
  //
  // The browser shows its own wording and ignores ours; preventDefault plus
  // a legacy returnValue is the whole API. It does not fire for the app's
  // own update reload, because that path refuses to run while anything is
  // held.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (planNavigation(summarise(currentHolds())) === null) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  if (blocker.state !== 'blocked' || !plan) return null;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4"
         role="alertdialog" aria-modal="true" aria-labelledby="navguard-title"
         data-testid="navigation-guard">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
          <div className="min-w-0">
            <h2 id="navguard-title" className="text-base font-semibold text-foreground">{plan.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{plan.body}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {/* Staying is the safe default and reads first. */}
          <Button size="sm" onClick={() => blocker.reset?.()} data-testid="navguard-stay">
            {t('recovery.stay', 'Stay on this page')}
          </Button>
          {/* ⚠️ Absent for pending work. Leaving cannot un-send a request
              that is already with the server, so offering "discard" would be
              a lie about what the button does. */}
          {plan.canDiscard && (
            <Button size="sm" variant="outline" onClick={() => blocker.proceed?.()}
                    data-testid="navguard-discard">
              {t('recovery.discardAndLeave', 'Discard and leave')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
